import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalize } from 'lodash';
import { IconButton, LinearProgress, TextField, Typography } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Close';
import ExpandIcon from '@material-ui/icons/ExpandMore';
import PrevIcon from '@material-ui/icons/ChevronLeft';
import NextIcon from '@material-ui/icons/ChevronRight';
import Button from '../../_ui/Button/Button';
import { ServiceProvidersApi } from '../../_helpers/service';
import { useStyles } from './ImportExcelStyle';

const PAGE_SIZE = 20;
const EMPTY_FILTER = { id: '', bin: '', name: '', iin: '' };

const toRequestFilter = (filter, lang, pageNumber) => {
  const result = { pageNumber, pageSize: PAGE_SIZE, rootsOnly: true, closed: false };
  if (filter.id) result.id = filter.id;
  if (filter.iin) result.iin = filter.iin;
  // текстовые фильтры, как в реестре услугодателей, применяются от 3 символов
  if (filter.bin.length > 2) result.bin = filter.bin;
  if (filter.name.length > 2) result[`name${capitalize(lang)}`] = filter.name;
  return result;
};

export const orgName = (org, lang) => org[`${lang}_name`] || org.ru_name || org.kk_name || '—';

/**
 * Выбор организации из реестра услугодателей (только корневые, без закрытых).
 * value — выбранная организация или null, onChange(org | null).
 */
export default function OrganizationPicker({ value, onChange }) {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const lang = i18n.language;
  const [open, setOpen] = useState(!value);
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [request, setRequest] = useState(() => toRequestFilter(EMPTY_FILTER, lang, 1));
  const [data, setData] = useState({ content: [], totalElements: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!open) return;
    let canceled = false;
    setLoading(true);
    setError(false);
    ServiceProvidersApi.loadTableData(JSON.stringify(request))
      .then(({ data }) => {
        if (canceled) return;
        if (data.status !== 'SUCCESS') throw new Error(data.status);
        setData(data.result);
      })
      .catch(() => !canceled && setError(true))
      .finally(() => !canceled && setLoading(false));
    return () => {
      canceled = true;
    };
  }, [open, request, reload]);

  useEffect(() => {
    const next = toRequestFilter(filter, lang, 1);
    const timeout = setTimeout(
      () => setRequest(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next)),
      500
    );
    return () => clearTimeout(timeout);
  }, [filter, lang]);

  const select = org => {
    onChange(org);
    setOpen(false);
  };

  if (value && !open) {
    return (
      <div>
        <div className={classes.orgLabel}>
          {t('importExcel_organization')} <span className={classes.required}>*</span>
        </div>
        <div
          role="button"
          tabIndex={0}
          aria-haspopup="listbox"
          className={classes.orgField}
          onClick={() => setOpen(true)}
          onKeyDown={e => e.key === 'Enter' && setOpen(true)}
        >
          <div className={classes.grow}>
            <Typography noWrap>{orgName(value, lang)}</Typography>
            <Typography variant="body2" color="textSecondary">
              {t('importExcel_bin')} {value.bin || '—'}
            </Typography>
          </div>
          <IconButton
            size="small"
            aria-label={t('importExcel_clearOrganization')}
            onClick={e => {
              e.stopPropagation();
              onChange(null);
              setOpen(true);
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" aria-label={t('importExcel_changeOrganization')}>
            <ExpandIcon />
          </IconButton>
        </div>
      </div>
    );
  }

  const pages = Math.max(1, Math.ceil(data.totalElements / PAGE_SIZE));
  const rows = data.content.filter(org => !org.closed);
  const filterField = (name, label, numeric) => (
    <div>
      <div className={classes.orgFilterLabel}>{label}</div>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        value={filter[name]}
        inputProps={numeric ? { inputMode: 'numeric' } : undefined}
        onChange={({ target }) =>
          setFilter({
            ...filter,
            [name]: numeric ? target.value.replace(/\D/g, '') : target.value
          })
        }
      />
    </div>
  );

  return (
    <div>
      <div className={classes.orgLegend}>
        {t('importExcel_organization')} <span className={classes.required}>*</span> —{' '}
        {t('importExcel_organizationSearch')}
      </div>
      <div className={classes.orgBox}>
        <div className={classes.orgFilters}>
          {filterField('id', t('importExcel_columnId'), true)}
          {filterField('bin', t('importExcel_bin'))}
          {filterField('name', t('importExcel_columnName'))}
          {filterField('iin', t('importExcel_columnIin'), true)}
        </div>
        {loading ? <LinearProgress /> : <div className={classes.orgLoaderSpacer} />}
        <div className={classes.orgTableWrap}>
          {error ? (
            <div className={classes.orgPlaceholder}>
              <Typography color="error">{t('importExcel_organizationsError')}</Typography>
              <Button
                variant="outlined"
                text={t('importExcel_retry')}
                onClick={() => setReload(reload + 1)}
              />
            </div>
          ) : !loading && rows.length === 0 ? (
            <div className={classes.orgPlaceholder}>
              <Typography color="textSecondary">{t('importExcel_organizationsEmpty')}</Typography>
            </div>
          ) : (
            <table className={classes.orgTable} role="listbox">
              <thead>
                <tr>
                  <th className={classes.orgColId}>{t('importExcel_columnId')}</th>
                  <th className={classes.orgColCode}>{t('importExcel_bin')}</th>
                  <th>{t('importExcel_columnName')}</th>
                  <th className={classes.orgColCode}>{t('importExcel_columnIin')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(org => (
                  <tr
                    key={org.id}
                    role="option"
                    aria-selected={!!value && value.id === org.id}
                    className={`${classes.orgRow} ${
                      value && value.id === org.id ? classes.orgRowSelected : ''
                    }`}
                    onClick={() => select(org)}
                  >
                    <td>{org.id}</td>
                    <td>{org.bin || '—'}</td>
                    <td title={orgName(org, lang)}>{orgName(org, lang)}</td>
                    <td>{org.iin || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className={classes.orgFooter}>
          <span className={classes.grow}>
            {t('importExcel_total')}: {data.totalElements} · {t('importExcel_rowHint')}
          </span>
          <span>
            {t('importExcel_page', { page: request.pageNumber, pages })} · {t('importExcel_perPage')}
          </span>
          <IconButton
            size="small"
            aria-label={t('importExcel_prevPage')}
            disabled={loading || request.pageNumber <= 1}
            onClick={() => setRequest({ ...request, pageNumber: request.pageNumber - 1 })}
          >
            <PrevIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={t('importExcel_nextPage')}
            disabled={loading || request.pageNumber >= pages}
            onClick={() => setRequest({ ...request, pageNumber: request.pageNumber + 1 })}
          >
            <NextIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
